import * as React from "react";
import Slider, { Settings } from "react-slick";
import {
  ConstBreakPoint,
  ConstCalc,
  ConstColor,
} from "src/config/styles_const/styles_const";
import { ClientBookmark } from "src/domain/model/client_bookmark";
import { Customer } from "src/domain/model/customer";
import { TJobCareerWithCustomerID } from "src/domain/repository/job_career_repository";
import BtnLinkBorderSmall from "src/presentation/common/atom/btn/btn_link_border_small";
import SlideNextArrow from "src/presentation/common/atom/slider_arrow/slide_next_arrow";
import SlidePrevArrow from "src/presentation/common/atom/slider_arrow/slide_prev_arrow";
import { CampaignMQa } from "src/presentation/common/molecular/campaign/campaign-qa";
import { CampaignMSection } from "src/presentation/common/molecular/campaign/campaign-section-title";
import styled from "styled-components";
import { routeConfig } from "../../../../config/language/ja-JP/presentation/route_text";
import HeadlineSmall from "../../../common/atom/text/headline_small";
import PageWrapper from "../../../common/atom/wrapper/page_wrapper";
import Breadcrumbs from "../../../common/molecular/breadcrumbs/breadcrumbs";
import Progress from "../../../common/organism/progress";
import { TBreadcrumbItem } from "../../../type/common";
import SlideCustomerCard from "../top_page/slide_customer_card";

interface IProps {
  cmoCustomers: Customer[];
  toCMarketingCustomers: Customer[];
  toBMarketingCustomers: Customer[];
  webMarketingCustomers: Customer[];
  snsMarketingCustomers: Customer[];
  contentMarketingCustomers: Customer[];
  seoCustomers: Customer[];
  dataMarketingCustomers: Customer[];
  eventMarketingCustomers: Customer[];
  tvcmMarketingCustomers: Customer[];
  jobCareersWithCustomerID: TJobCareerWithCustomerID[];
  clientBookmarks: ClientBookmark[];
  isLoading: boolean;
}

// breackpoint は調整必要
const settings: Settings = {
  className: "slider",
  dots: false,
  infinite: false,
  centerMode: false,
  swipeToSlide: true,
  variableWidth: true,
  arrows: true,
  prevArrow: <SlidePrevArrow />,
  nextArrow: <SlideNextArrow />,
  slidesToShow: 4,
  slidesToScroll: 4,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 3,
      },
    },
    {
      breakpoint: 650,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
      },
    },

    {
      breakpoint: 450,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

// breackpoint は調整必要
const sectionSettings: Settings = {
  className: "slider",
  dots: false,
  infinite: false,
  centerMode: false,
  swipeToSlide: true,
  variableWidth: true,
  arrows: true,
  prevArrow: <SlidePrevArrow />,
  nextArrow: <SlideNextArrow />,
  slidesToShow: 2,
  slidesToScroll: 2,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
      },
    },
    {
      breakpoint: 650,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
      },
    },

    {
      breakpoint: 450,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

const ClientMarketingCampaignComponent: React.FC<IProps> = (props) => {
  const {
    cmoCustomers,
    toCMarketingCustomers,
    toBMarketingCustomers,
    webMarketingCustomers,
    snsMarketingCustomers,
    contentMarketingCustomers,
    seoCustomers,
    dataMarketingCustomers,
    eventMarketingCustomers,
    tvcmMarketingCustomers,
    jobCareersWithCustomerID,
    clientBookmarks,
    isLoading,
  } = props;

  const breadcrumbItems: TBreadcrumbItem[] = [
    routeConfig.clientRoot,
    routeConfig.clientPmCampaign,
  ];

  const res: JSX.Element = (
    <React.Fragment>
      {isLoading && <Progress canAnimation={false} />}
      <PageWrapper>
        <Breadcrumbs breadcrumbItems={breadcrumbItems} />

        <CampaignMQa
          q="一人のマーケターがマーケティングの全てを網羅できているわけではない"
          a="マーケティングといっても、扱うプロダクトやプロモーション手段、プロダクトの成長フェーズによって業務内容は様々です。そのため経験してきた業界や扱うプロダクトによって得意不得意があります。"
        />

        <div style={{ width: "100%", marginTop: 70 }} />

        <CampaignMSection
          index={"1"}
          title={"会社や事業のマーケティング戦略を担うCMO"}
          text={
            "一つのプロダクトやサービスの売上向上を目的としたプロモーションではなく、会社全体や事業全体の拡大戦略におけるマーケティング組織をマネジメントできるのがCMOに求められます。"
          }
        />

        <PmWrapper>
          <SubTitleWrapper>
            <HeadlineSmall />
            <BtnLinkBorderSmall
              to={`/client/scout/customers?q=CMO&p=1`}
              target="_blank"
            >
              もっと見る
            </BtnLinkBorderSmall>
          </SubTitleWrapper>

          <SliderWrapper>
            <Slider {...settings}>
              {cmoCustomers.map((customer) => {
                const jobCareerWithCustomerID = jobCareersWithCustomerID.find(
                  (_jobCareerWithCustomerID) => {
                    return _jobCareerWithCustomerID.customerID === customer.id;
                  }
                );
                const jobCareers = jobCareerWithCustomerID
                  ? jobCareerWithCustomerID.jobCareers
                  : [];
                const isBookmarked: boolean = clientBookmarks.some(
                  (clientBookmark: ClientBookmark) => {
                    return clientBookmark.customerID === customer.id;
                  }
                );

                return (
                  <SlideCustomerWrapper
                    key={customer.id}
                    style={{ width: 200 }}
                  >
                    <SlideCustomerCard
                      customer={customer}
                      jobCareers={jobCareers}
                      isBookmarked={isBookmarked}
                    />
                  </SlideCustomerWrapper>
                );
              })}
            </Slider>
          </SliderWrapper>
        </PmWrapper>

        <Border />

        <CampaignMSection
          index={"2"}
          title={"プロダクト、サービスがBtoCかBtoBによって購入意思決定が異なる"}
          text={
            "toCの場合、利用者と購入決定者が同一人物で、購入意思決定のスピードが速いのが特徴。合理性だけでなく感情面でも購入されるケースがあります。m一方toBでは、利用者と購入決定者が異なることがほとんどで、企業内の部署間で調整が必要なため、購入までに時間がかかります。企業にとっての収益性を重視するため、購買には合理的な判断がされます。\nこのように購入プロセスが異なるため、マーケティング戦略も異なってきます。"
          }
        />

        <CardsFlexWrapper>
          <CardWrapper>
            <SubTitleWrapper>
              <HeadlineSmall>BtoC</HeadlineSmall>
              <BtnLinkBorderSmall
                to={`/client/scout/customers?q=マーケティング　BtoC&p=1`}
                target="_blank"
              >
                もっと見る
              </BtnLinkBorderSmall>
            </SubTitleWrapper>

            <SliderWrapper>
              <Slider {...sectionSettings}>
                {toCMarketingCustomers.map((customer) => {
                  const jobCareerWithCustomerID = jobCareersWithCustomerID.find(
                    (_jobCareerWithCustomerID) => {
                      return (
                        _jobCareerWithCustomerID.customerID === customer.id
                      );
                    }
                  );
                  const jobCareers = jobCareerWithCustomerID
                    ? jobCareerWithCustomerID.jobCareers
                    : [];
                  const isBookmarked: boolean = clientBookmarks.some(
                    (clientBookmark: ClientBookmark) => {
                      return clientBookmark.customerID === customer.id;
                    }
                  );

                  return (
                    <SectionSlideCustomerWrapper
                      key={customer.id}
                      style={{ width: 200 }}
                    >
                      <SlideCustomerCard
                        customer={customer}
                        jobCareers={jobCareers}
                        isBookmarked={isBookmarked}
                      />
                    </SectionSlideCustomerWrapper>
                  );
                })}
              </Slider>
            </SliderWrapper>
          </CardWrapper>

          <CardWrapper>
            <SubTitleWrapper>
              <HeadlineSmall>BtoB</HeadlineSmall>
              <BtnLinkBorderSmall
                to={`/client/scout/customers?q=マーケティング　BtoB&p=1`}
                target="_blank"
              >
                もっと見る
              </BtnLinkBorderSmall>
            </SubTitleWrapper>

            <SliderWrapper>
              <Slider {...sectionSettings}>
                {toBMarketingCustomers.map((customer) => {
                  const jobCareerWithCustomerID = jobCareersWithCustomerID.find(
                    (_jobCareerWithCustomerID) => {
                      return (
                        _jobCareerWithCustomerID.customerID === customer.id
                      );
                    }
                  );
                  const jobCareers = jobCareerWithCustomerID
                    ? jobCareerWithCustomerID.jobCareers
                    : [];
                  const isBookmarked: boolean = clientBookmarks.some(
                    (clientBookmark: ClientBookmark) => {
                      return clientBookmark.customerID === customer.id;
                    }
                  );

                  return (
                    <SectionSlideCustomerWrapper
                      key={customer.id}
                      style={{ width: 200 }}
                    >
                      <SlideCustomerCard
                        customer={customer}
                        jobCareers={jobCareers}
                        isBookmarked={isBookmarked}
                      />
                    </SectionSlideCustomerWrapper>
                  );
                })}
              </Slider>
            </SliderWrapper>
          </CardWrapper>
        </CardsFlexWrapper>

        <Border />

        <CampaignMSection
          index={"3"}
          title={"多岐に渡る webマーケティング"}
          text={
            "Web（インターネット）を利用したマーケティング活動のことで、Webを利用して売れる仕組みを作る一連の取り組みを指します。SEO、インターネット広告、コンテンツマーケティング、SNSなど、Webマーケティングの施策は多岐にわたります。"
          }
        />

        <CardsFlexWrapper>
          <CardWrapper>
            <SubTitleWrapper>
              <HeadlineSmall>Web広告</HeadlineSmall>
              <BtnLinkBorderSmall
                to={`/client/scout/customers?q=マーケティング　Web&p=1`}
                target="_blank"
              >
                もっと見る
              </BtnLinkBorderSmall>
            </SubTitleWrapper>

            <SliderWrapper>
              <Slider {...sectionSettings}>
                {webMarketingCustomers.map((customer) => {
                  const jobCareerWithCustomerID = jobCareersWithCustomerID.find(
                    (_jobCareerWithCustomerID) => {
                      return (
                        _jobCareerWithCustomerID.customerID === customer.id
                      );
                    }
                  );
                  const jobCareers = jobCareerWithCustomerID
                    ? jobCareerWithCustomerID.jobCareers
                    : [];
                  const isBookmarked: boolean = clientBookmarks.some(
                    (clientBookmark: ClientBookmark) => {
                      return clientBookmark.customerID === customer.id;
                    }
                  );

                  return (
                    <SectionSlideCustomerWrapper
                      key={customer.id}
                      style={{ width: 200 }}
                    >
                      <SlideCustomerCard
                        customer={customer}
                        jobCareers={jobCareers}
                        isBookmarked={isBookmarked}
                      />
                    </SectionSlideCustomerWrapper>
                  );
                })}
              </Slider>
            </SliderWrapper>
          </CardWrapper>

          <CardWrapper>
            <SubTitleWrapper>
              <HeadlineSmall>SNS</HeadlineSmall>
              <BtnLinkBorderSmall
                to={`/client/scout/customers?q=マーケティング　SNS&p=1`}
                target="_blank"
              >
                もっと見る
              </BtnLinkBorderSmall>
            </SubTitleWrapper>

            <SliderWrapper>
              <Slider {...sectionSettings}>
                {snsMarketingCustomers.map((customer) => {
                  const jobCareerWithCustomerID = jobCareersWithCustomerID.find(
                    (_jobCareerWithCustomerID) => {
                      return (
                        _jobCareerWithCustomerID.customerID === customer.id
                      );
                    }
                  );
                  const jobCareers = jobCareerWithCustomerID
                    ? jobCareerWithCustomerID.jobCareers
                    : [];
                  const isBookmarked: boolean = clientBookmarks.some(
                    (clientBookmark: ClientBookmark) => {
                      return clientBookmark.customerID === customer.id;
                    }
                  );

                  return (
                    <SectionSlideCustomerWrapper
                      key={customer.id}
                      style={{ width: 200 }}
                    >
                      <SlideCustomerCard
                        customer={customer}
                        jobCareers={jobCareers}
                        isBookmarked={isBookmarked}
                      />
                    </SectionSlideCustomerWrapper>
                  );
                })}
              </Slider>
            </SliderWrapper>
          </CardWrapper>
        </CardsFlexWrapper>

        <div style={{ width: "100%", marginTop: 40 }} />

        <CardsFlexWrapper>
          <CardWrapper>
            <SubTitleWrapper>
              <HeadlineSmall>コンテンツ</HeadlineSmall>
              <BtnLinkBorderSmall
                to={`/client/scout/customers?q=コンテンツマーケティング&p=1`}
                target="_blank"
              >
                もっと見る
              </BtnLinkBorderSmall>
            </SubTitleWrapper>

            <SliderWrapper>
              <Slider {...sectionSettings}>
                {contentMarketingCustomers.map((customer) => {
                  const jobCareerWithCustomerID = jobCareersWithCustomerID.find(
                    (_jobCareerWithCustomerID) => {
                      return (
                        _jobCareerWithCustomerID.customerID === customer.id
                      );
                    }
                  );
                  const jobCareers = jobCareerWithCustomerID
                    ? jobCareerWithCustomerID.jobCareers
                    : [];
                  const isBookmarked: boolean = clientBookmarks.some(
                    (clientBookmark: ClientBookmark) => {
                      return clientBookmark.customerID === customer.id;
                    }
                  );

                  return (
                    <SectionSlideCustomerWrapper
                      key={customer.id}
                      style={{ width: 200 }}
                    >
                      <SlideCustomerCard
                        customer={customer}
                        jobCareers={jobCareers}
                        isBookmarked={isBookmarked}
                      />
                    </SectionSlideCustomerWrapper>
                  );
                })}
              </Slider>
            </SliderWrapper>
          </CardWrapper>

          <CardWrapper>
            <SubTitleWrapper>
              <HeadlineSmall>SEO</HeadlineSmall>
              <BtnLinkBorderSmall
                to={`/client/scout/customers?q=SEO&p=1`}
                target="_blank"
              >
                もっと見る
              </BtnLinkBorderSmall>
            </SubTitleWrapper>

            <SliderWrapper>
              <Slider {...sectionSettings}>
                {seoCustomers.map((customer) => {
                  const jobCareerWithCustomerID = jobCareersWithCustomerID.find(
                    (_jobCareerWithCustomerID) => {
                      return (
                        _jobCareerWithCustomerID.customerID === customer.id
                      );
                    }
                  );
                  const jobCareers = jobCareerWithCustomerID
                    ? jobCareerWithCustomerID.jobCareers
                    : [];
                  const isBookmarked: boolean = clientBookmarks.some(
                    (clientBookmark: ClientBookmark) => {
                      return clientBookmark.customerID === customer.id;
                    }
                  );

                  return (
                    <SectionSlideCustomerWrapper
                      key={customer.id}
                      style={{ width: 200 }}
                    >
                      <SlideCustomerCard
                        customer={customer}
                        jobCareers={jobCareers}
                        isBookmarked={isBookmarked}
                      />
                    </SectionSlideCustomerWrapper>
                  );
                })}
              </Slider>
            </SliderWrapper>
          </CardWrapper>
        </CardsFlexWrapper>

        <Border />

        <CampaignMSection
          index={"4"}
          title={"webマーケティングとは異なるデータマーケティング"}
          text={
            "モバイルアプリの行動履歴、位置情報、性別・年齢・職業などのあらゆるデータを活用したマーケティングを指しています。webマーケティングと大きく異なる点は、データを活用した予測モデルの使用、BIツールを使った分析などの業務があるところです。"
          }
        />

        <PmWrapper>
          <SubTitleWrapper>
            <HeadlineSmall />
            <BtnLinkBorderSmall
              to={`/client/scout/customers?q=データ分析　マーケティング&p=1`}
              target="_blank"
            >
              もっと見る
            </BtnLinkBorderSmall>
          </SubTitleWrapper>

          <SliderWrapper>
            <Slider {...settings}>
              {dataMarketingCustomers.map((customer) => {
                const jobCareerWithCustomerID = jobCareersWithCustomerID.find(
                  (_jobCareerWithCustomerID) => {
                    return _jobCareerWithCustomerID.customerID === customer.id;
                  }
                );
                const jobCareers = jobCareerWithCustomerID
                  ? jobCareerWithCustomerID.jobCareers
                  : [];
                const isBookmarked: boolean = clientBookmarks.some(
                  (clientBookmark: ClientBookmark) => {
                    return clientBookmark.customerID === customer.id;
                  }
                );

                return (
                  <SlideCustomerWrapper
                    key={customer.id}
                    style={{ width: 200 }}
                  >
                    <SlideCustomerCard
                      customer={customer}
                      jobCareers={jobCareers}
                      isBookmarked={isBookmarked}
                    />
                  </SlideCustomerWrapper>
                );
              })}
            </Slider>
          </SliderWrapper>
        </PmWrapper>

        <Border />

        <CampaignMSection
          index={"5"}
          title={"オフラインマーケティング"}
          text={
            "web環境が常態化した現代でも、オフラインマーケティングが有効なフェーズや業界があります。\nプロダクトの売上がある程度までいくと成長が鈍化し、さらに成長させるにはターゲットの幅を広げる必要が出てきます。そんな時にマスメディアの活用や展示会やイベント出展をすることでweb環境とは異なる接点を作ることができいます。"
          }
        />

        <CardsFlexWrapper>
          <CardWrapper>
            <SubTitleWrapper>
              <HeadlineSmall>展示会、イベント</HeadlineSmall>
              <BtnLinkBorderSmall
                to={`/client/scout/customers?q=イベント　マーケティング&p=1`}
                target="_blank"
              >
                もっと見る
              </BtnLinkBorderSmall>
            </SubTitleWrapper>

            <SliderWrapper>
              <Slider {...sectionSettings}>
                {eventMarketingCustomers.map((customer) => {
                  const jobCareerWithCustomerID = jobCareersWithCustomerID.find(
                    (_jobCareerWithCustomerID) => {
                      return (
                        _jobCareerWithCustomerID.customerID === customer.id
                      );
                    }
                  );
                  const jobCareers = jobCareerWithCustomerID
                    ? jobCareerWithCustomerID.jobCareers
                    : [];
                  const isBookmarked: boolean = clientBookmarks.some(
                    (clientBookmark: ClientBookmark) => {
                      return clientBookmark.customerID === customer.id;
                    }
                  );

                  return (
                    <SectionSlideCustomerWrapper
                      key={customer.id}
                      style={{ width: 200 }}
                    >
                      <SlideCustomerCard
                        customer={customer}
                        jobCareers={jobCareers}
                        isBookmarked={isBookmarked}
                      />
                    </SectionSlideCustomerWrapper>
                  );
                })}
              </Slider>
            </SliderWrapper>
          </CardWrapper>

          <CardWrapper>
            <SubTitleWrapper>
              <HeadlineSmall>TVCM、広告など</HeadlineSmall>
              <BtnLinkBorderSmall
                to={`/client/scout/customers?q=テレビCM&p=1`}
                target="_blank"
              >
                もっと見る
              </BtnLinkBorderSmall>
            </SubTitleWrapper>

            <SliderWrapper>
              <Slider {...sectionSettings}>
                {tvcmMarketingCustomers.map((customer) => {
                  const jobCareerWithCustomerID = jobCareersWithCustomerID.find(
                    (_jobCareerWithCustomerID) => {
                      return (
                        _jobCareerWithCustomerID.customerID === customer.id
                      );
                    }
                  );
                  const jobCareers = jobCareerWithCustomerID
                    ? jobCareerWithCustomerID.jobCareers
                    : [];
                  const isBookmarked: boolean = clientBookmarks.some(
                    (clientBookmark: ClientBookmark) => {
                      return clientBookmark.customerID === customer.id;
                    }
                  );

                  return (
                    <SectionSlideCustomerWrapper
                      key={customer.id}
                      style={{ width: 200 }}
                    >
                      <SlideCustomerCard
                        customer={customer}
                        jobCareers={jobCareers}
                        isBookmarked={isBookmarked}
                      />
                    </SectionSlideCustomerWrapper>
                  );
                })}
              </Slider>
            </SliderWrapper>
          </CardWrapper>
        </CardsFlexWrapper>
      </PageWrapper>
    </React.Fragment>
  );

  return res;
};

export default ClientMarketingCampaignComponent;

const PmWrapper = styled.div`
  width: 100%;
  padding-bottom: 15px;
`;

const SubTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  margin-bottom: 15px;
  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    margin-bottom: 30px;
  }
`;

const CardsFlexWrapper = styled.div`
  display: block;
  width: 100%;
  // border-bottom: 1px solid ${ConstColor.LightGray};
  // margin-bottom: 45px;
  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    display: flex;
  }
`;

const Border = styled.div`
  width: 100%;
  margin-top: 54px;
  margin-bottom: 47px;
  border-bottom: 1px solid ${ConstColor.LightGray};
`;

const CardWrapper = styled.div`
  width: 100%;
  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    width: 45%;
    margin-right: 5%;
  }
`;

const SliderWrapper = styled.div`
  margin-bottom: 40px;

  // react-slickの横幅の計算がうまく働いていないのでここで決めている感じ
  max-width: ${ConstCalc.CustomerSlideMaxWidthSp};
  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    margin-bottom: 40px;
    max-width: ${ConstCalc.CustomerSlideMaxWidthTablet};
  }
  @media screen and (min-width: ${ConstBreakPoint.PcDeskTop}) {
    max-width: ${ConstCalc.CustomerSlideMaxWidthPc};
  }

  // 一部で縦の大きさが変動していたため固定する
  & .slick-slider {
    height: 334px;
  }
`;

const SlideCustomerWrapper = styled.div`
  padding: 5px 15px;
`;

const SectionSlideCustomerWrapper = styled.div`
  padding: 5px 15px 5px 5px;
`;



// WEBPACK FOOTER //
// ./src/presentation/component/client/campaign/client_marketing_campaign.tsx